import React from 'react';
import { SectionCard } from './SectionCard';
import { SHARED_CHANGE_LABELS } from './constants';
import { changesSinceReturn, amendmentChangesSinceReturn } from './changeDiff';

// "What did they change after I sent it back?"
//
// Sits directly under StatusActionsCard on every detail page. Unlike History
// it is OPEN by default and shows only the net change since the last return —
// the reviewer's actual question when a re-submitted request lands back in
// their queue. It renders nothing at all when the request was never returned,
// or when the requester re-submitted without touching anything.
//
// Props mirror HistoryCard's on purpose: a page passes the same changeLabels /
// fmtChangeValue / expandChanges it already passes there, so the two tables
// can never disagree about how a field is named or formatted.
//
// Why this is the roll-out vehicle for all fifteen flows: it reports whatever
// the diff contains, so it CANNOT silently miss a field the way per-field
// inline wiring can. The inline strike-through (DetailItem `field=`) is the
// finer treatment layered on top where the values are actually displayed.
const defaultFmt = (field, v) => (v === null || v === undefined || v === '' ? '—' : String(v));

const ChangesSinceReturnCard = ({
  events = [],
  changeLabels = {},
  fmtChangeValue = defaultFmt,
  expandChanges = null,
  // qca / car log the amendment payload rather than an edit delta — see
  // changeDiff.amendmentChangesSinceReturn. Those two pages set this.
  amendment = false,
}) => {
  const raw = amendment ? amendmentChangesSinceReturn(events) : changesSinceReturn(events);
  if (!raw) return null;
  const diff = expandChanges ? expandChanges(raw) : raw;
  const rows = Object.entries(diff);
  if (rows.length === 0) return null;
  const labels = { ...SHARED_CHANGE_LABELS, ...changeLabels };

  return (
    <SectionCard
      title="Changes Since It Was Returned"
      count={`${rows.length} field${rows.length === 1 ? '' : 's'}`}
    >
      {/* table-fixed + break-words: a pasted long value must wrap inside its
          column, never widen the table past the card (workflow-ui.md §4.4). */}
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500">
            <th className="py-1.5 pr-3 font-medium w-1/3">Field</th>
            <th className="py-1.5 pr-3 font-medium w-1/3">When You Returned It</th>
            <th className="py-1.5 font-medium w-1/3">Now</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map(([field, d]) => (
            <tr key={field}>
              <td className="py-1.5 pr-3 text-slate-700 align-top break-words">
                {d.label || labels[field] || field}
              </td>
              <td className="py-1.5 pr-3 text-slate-500 line-through align-top break-words">
                {fmtChangeValue(field, d.before)}
              </td>
              <td className="py-1.5 text-slate-900 font-medium align-top break-words">
                {fmtChangeValue(field, d.after)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
};

export default ChangesSinceReturnCard;
